/**
 * Apollo cache adapters for in-place cache reconciliation — no full refetch needed.
 *
 * reconcileGroupsInCache — folder (Group) creates / renames / deletes: inserts at
 *   sorted position, updates in place, or removes by id on the shared `groups` field.
 *
 * reconcileItemsInCache — item (Asset, Playlist, Schedule, Device) creates: inserts the
 *   new item at position 0 of every matching list cache entry (assets / playlists /
 *   schedules / devices). For screens, the caller must first writeFragment to seed
 *   Device:<id> because pairDevice returns DeviceDetailResponse (__typename mismatch).
 *
 * Both share the same internal helpers (CacheEdge, CacheConnection, parseStoreArgs,
 * normalizePath, withEdges) defined once below.
 *
 * These are OPTIMISTIC in-memory updates. Callers' background subscription refetches
 * are authoritative — a mis-matched entry is corrected on the next refetch.
 */
import { matchesAssetTypeFilter } from '@/lib/asset-type-filter';
import type { ApolloCache, Reference } from '@apollo/client';
import { compareFolderNodes, type FolderNodeLike, type FolderSort } from './reconcileGroups';

/** Which cached `groups` list entries this change belongs to. */
export interface GroupMatch {
  /** Group type filter: 'ASSET' | 'DEVICE' | 'PLAYLIST' | 'SCHEDULE'. */
  type: string;
  /** Parent path the folder lives under (null/'' = root). */
  path: string | null;
}

export interface ReconcileGroupsInput {
  /** Node to insert (absent) or that changed (present); needs the sort fields. */
  changedNode?: FolderNodeLike;
  /** `_id` to remove. */
  removedId?: string;
  match: GroupMatch;
  sort: FolderSort;
}

interface CacheEdge {
  __typename?: string;
  cursor?: string;
  node: Reference;
}

interface CacheConnection {
  __typename?: string;
  page?: { __typename?: string; edges?: CacheEdge[]; pageInfo?: unknown } | null;
  pageData?: { __typename?: string; total?: number } | null;
}

/** Extract the JSON args object encoded in an Apollo storeFieldName, e.g. `groups({...})`. */
function parseStoreArgs(storeFieldName: string): Record<string, unknown> | null {
  const open = storeFieldName.indexOf('(');
  const close = storeFieldName.lastIndexOf(')');
  if (open < 0 || close <= open) return null;
  try {
    return JSON.parse(storeFieldName.slice(open + 1, close));
  } catch {
    return null;
  }
}

function normalizePath(p: unknown): string | null {
  return p == null || p === '' ? null : String(p);
}

/**
 * Reconcile one folder change into every matching cached `groups` entry.
 * No-op (leaves the entry untouched) for entries that don't match `match`,
 * for search views (`name_contains`), and for favorites-only views.
 */
export function reconcileGroupsInCache(
  cache: ApolloCache,
  { changedNode, removedId, match, sort }: ReconcileGroupsInput,
): void {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      groups(value, { storeFieldName, readField, toReference }) {
        const existing = value as CacheConnection;
        const edges = existing?.page?.edges;
        if (!Array.isArray(edges)) return value;

        // ── Only touch the entry this change belongs to ──
        const args = parseStoreArgs(storeFieldName);
        const query = (args?.query ?? {}) as Record<string, unknown>;
        if (query.type !== match.type) return value;
        if (normalizePath(query.path) !== normalizePath(match.path)) return value;
        // Search + favorites views are filtered projections — leave them to their own
        // (network-only) refresh rather than unconditionally injecting a row.
        if (query.name_contains != null) return value;
        // Exact-name system projections (e.g. the Screens page's separate
        // `{name:"OnHold Devices"}` query, useScreens.ts) share this same `groups` field
        // with type:DEVICE, path:null but are NOT the plain folder list — injecting the new
        // folder here made it render a SECOND time when useScreens merges that edge into the
        // list (duplicate folder on create). The plain list never sets `query.name`.
        if (query.name != null) return value;
        const extendParams = (args?.extendParams ?? {}) as Record<string, unknown>;
        if (extendParams.favoritesOnly === true) return value;

        const idOf = (edge: CacheEdge): string => (readField('_id', edge.node) as string) ?? '';

        // ── Remove ──
        if (removedId != null) {
          const next = edges.filter((e) => idOf(e) !== removedId);
          if (next.length === edges.length) return value;
          return withEdges(existing, next, -1) as typeof value;
        }

        if (!changedNode) return value;

        // ── Update in place: the node is a normalized Group:<id> reference, so the
        // mutation write already refreshed its fields everywhere. Nothing to do to the
        // edge array (position is preserved; the background sync repositions if the
        // rename changed sort order). ──
        if (edges.some((e) => idOf(e) === changedNode._id)) return value;

        // ── Insert at sorted position ──
        const nodeLikeAt = (edge: CacheEdge): FolderNodeLike => ({
          _id: idOf(edge),
          name: readField('name', edge.node) as string | null | undefined,
          createdAt: readField('createdAt', edge.node) as string | null | undefined,
          lastUpdatedDate: readField('lastUpdatedDate', edge.node) as string | null | undefined,
        });
        let insertAt = edges.length;
        for (let i = 0; i < edges.length; i++) {
          if (compareFolderNodes(changedNode, nodeLikeAt(edges[i]), sort) < 0) {
            insertAt = i;
            break;
          }
        }
        const ref = toReference({ __typename: 'Group', _id: changedNode._id });
        if (!ref) return value;
        const newEdge: CacheEdge = {
          __typename: edges[0]?.__typename,
          cursor: `__reconciled__:${changedNode._id}`,
          node: ref,
        };
        const next = edges.slice();
        next.splice(insertAt, 0, newEdge);
        return withEdges(existing, next, 1) as typeof value;
      },
    },
  });
}

function withEdges(
  existing: CacheConnection,
  edges: CacheEdge[],
  totalDelta: number,
): CacheConnection {
  const pageData = existing.pageData
    ? { ...existing.pageData, total: Math.max(0, (existing.pageData.total ?? 0) + totalDelta) }
    : existing.pageData;
  return {
    ...existing,
    page: { ...existing.page, edges },
    pageData,
  };
}

// ─── Item remove ─────────────────────────────────────────────────────────────

export interface RemoveItemsInput {
  /** ROOT_QUERY field: 'assets' | 'playlists' | 'schedules' | 'devices'. */
  rootField: string;
  /** IDs to remove from every cached list entry for this field. */
  removedIds: string[];
}

/**
 * Remove deleted items from every cached list entry for `rootField` using
 * cache.modify — the inverse of reconcileItemsInCache.
 *
 * Unlike cache.evict, this never leaves dangling __refs in the edge array.
 * Apollo can read every cached entry cleanly after this call, so there is no
 * incomplete-query flash. gc() is called after to drop the now-unreachable entities.
 *
 * No path/teamId matching is needed here — a deleted item should be gone from
 * every list variant (all `first`/`after`/`query` argument combinations).
 */
export function removeItemsFromCache(
  cache: ApolloCache,
  { rootField, removedIds }: RemoveItemsInput,
): void {
  if (!removedIds.length) return;
  const removedSet = new Set(removedIds);
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [rootField](value, { readField }) {
        const existing = value as CacheConnection;
        const edges = existing?.page?.edges;
        if (!Array.isArray(edges)) return value;
        const idOf = (edge: CacheEdge): string => (readField('_id', edge.node) as string) ?? '';
        const next = edges.filter((e) => !removedSet.has(idOf(e)));
        if (next.length === edges.length) return value;
        return withEdges(existing, next, next.length - edges.length) as typeof value;
      },
    },
  });
  cache.gc();
}

// ─── Item reconcile ───────────────────────────────────────────────────────────

/** Which cached item list entry to insert into. */
export interface ItemMatch {
  /** Folder the item was created in. null = root. */
  path: string | null;
  /** User's active teamId — matches the top-level teamId arg on the list query. */
  teamId: string;
  /** true only for the Engage page — separates engage=true assets entries from the
   *  main Assets page entries which share the same 'assets' ROOT_QUERY field. */
  engage?: boolean;
}

export interface ReconcileItemsInput {
  /** ROOT_QUERY field: 'assets' | 'playlists' | 'schedules' | 'devices'. */
  rootField: string;
  /** Apollo __typename for the new node: 'Asset' | 'Playlist' | 'Schedule' | 'Device'. */
  newItemTypename: string;
  newItemId: string;
  match: ItemMatch;
}

/**
 * Insert a newly created item at position 0 of every matching list cache entry.
 * Mirrors reconcileGroupsInCache but for items (no sorted insert — always prepend).
 * Skips search views, favorites, wrong path/teamId, engage mismatch, and entries
 * where the item is already at position 0 (idempotent).
 *
 * For Device items: caller must have written Device:<id> via cache.writeFragment first
 * (pairDevice returns DeviceDetailResponse so Apollo can't auto-normalize Device:<id>).
 */
export function reconcileItemsInCache(
  cache: ApolloCache,
  { rootField, newItemTypename, newItemId, match }: ReconcileItemsInput,
): void {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [rootField](value, { storeFieldName, readField, toReference }) {
        const existing = value as CacheConnection;
        const edges = existing?.page?.edges;
        if (!Array.isArray(edges)) return value;

        const args = parseStoreArgs(storeFieldName);
        if (!args) return value;

        const query = (args.query ?? {}) as Record<string, unknown>;
        const extendParams = (args.extendParams ?? {}) as Record<string, unknown>;

        // Skip search views — they are filtered projections, not the full list.
        if (query.name_contains != null) return value;
        // Skip favorites-only views.
        if (extendParams.favoritesOnly === true) return value;
        // Skip wrong teamId.
        if (args.teamId !== match.teamId) return value;
        // Skip wrong folder.
        if (normalizePath(query.path) !== normalizePath(match.path)) return value;
        // Separate Engage (query.engage === true) from the main Assets page.
        if (Boolean(query.engage) !== Boolean(match.engage)) return value;

        const idOf = (edge: CacheEdge): string => (readField('_id', edge.node) as string) ?? '';

        // Already at position 0 — nothing to do.
        if (edges.length > 0 && idOf(edges[0]) === newItemId) return value;

        const ref = toReference({ __typename: newItemTypename, _id: newItemId });
        if (!ref) return value;

        // A folder/team match alone does not make an asset eligible for a typed picker.
        // Read the normalized asset, not the list's old rows, before optimistic insertion.
        if (
          rootField === 'assets' &&
          newItemTypename === 'Asset' &&
          !matchesAssetTypeFilter(
            {
              type: readField<string | null>('type', ref),
              fileType: readField<string | null>('fileType', ref),
              appType: readField<string | null>('appType', ref),
              commonType: readField<string | null>('commonType', ref),
            },
            query,
          )
        )
          return value;

        const newEdge: CacheEdge = {
          __typename: edges[0]?.__typename,
          cursor: newItemId,
          node: ref,
        };
        // De-dup: remove any existing edge with the same id, then prepend.
        const deduped = edges.filter((e) => idOf(e) !== newItemId);
        const alreadyExisted = deduped.length < edges.length;
        return withEdges(existing, [newEdge, ...deduped], alreadyExisted ? 0 : 1) as typeof value;
      },
    },
  });
}
